import { observable, action, computed } from 'mobx';
import { IntlMessage } from 'react-intl/intl-message';
import { defineMessages } from 'react-intl';

import { FieldData, Validator, ValidationError } from './form';

export class ArrayOfFieldData extends FieldData {
    @observable fields: FieldData<any>[] = [];
    @observable visited = false;

    @observable initialFields;

    constructor(options = {}) {
        const { fields } = options;
        super(options);

        if (fields) {
            this.initialFields = [...fields];
            this.fields.replace(fields);
        } else {
            this.initialFields = [];
        }
    }

    computeValid() {
        if (super.computeValid() === false) {
            return false;
        }

        for (let i = 0; i < this.fields.length; i++) {
            if (!this.fields[i].valid) {
                return false;
            }
        }

        return true;
    }

    computeDirty(): boolean {
        if (this.fields.length !== this.initialFields.length) {
            return true;
        }

        for (let i = 0; i < this.fields.length; i++) {
            if (this.fields[i].dirty) {
                return true;
            }

            if (this.fields[i] !== this.initialFields[i]) {
                return true;
            }
        }

        return false;
    }

    cancel() {
        return Promise.all(this.fields.map(field => field.cancel()));
    }

    push(...fields) {
        this.visited = true;
        this.fields.push(...fields);
    }

    remove(field) {
        this.visited = true;
        this.fields.remove(field);

        return field.cancel();
    }

    move = ({ from, to }) => {
        this.visited = true;
        const items = this.fields.slice();
        const value = items[from];

        items.splice(from, 1);
        items.splice(to, 0, value);

        this.fields.replace(items);
    };

    replace(fields, cancel = true) {
        this.visited = true;
        const result = cancel ? this.cancel() : Promise.resolve();
        this.fields.replace(fields);

        return result;
    }

    removeLastValue() {
        this.visited = true;

        if (this.fields.length) {
            const field = this.fields.pop();
            return field.cancel();
        }

        return Promise.resolve();
    }

    reset(options) {
        this.visited = false;

        const { cancel } = options;
        const result = this.replace(this.initialFields, cancel);

        this.fields.forEach(field => {
            field.reset({ cancel });
        });

        return result;
    }

    @action resetAfterSubmit() {
        this.initialFields = this.fields;
        this.fields.forEach(field => {
            field.resetAfterSubmit();
        });
    }

    submitData(): U {
        return this.fields.map(field => field.submitData());
    }
}

export class NotEmptyValidator extends Validator<ArrayOfFieldData> {
    intlMessage: ?IntlMessage;

    constructor(options) {
        super(options);
        const { intlMessage } = options || {};
        this.intlMessage = intlMessage;
    }

    @computed get error() {
        const {
            fieldData: { fields },
        } = this;

        if (!fields.length) {
            return new ValidationError({
                intlMessage:
                    this.intlMessage ||
                    new IntlMessage({
                        message: defineMessages({
                            FORM_ERROR_FIELD_MUST_HAVE_ITEMS: { id: 'FORM_ERROR_FIELD_MUST_HAVE_ITEMS' },
                        }).FORM_ERROR_FIELD_MUST_HAVE_ITEMS,
                    }),
            });
        }

        return null;
    }
}
